<script setup lang="ts">
import { computed } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {formatCultureDate} from '@/use/formatCultureValue';

const props = defineProps<{
  name: string;
  type?: string;
  uploadedAt?: Date;
  size?: number;
  link?: string;
  clickable?: boolean;
}>();

const emit = defineEmits<{
  click: [];
}>();

const extension = computed(() => props.type ?? props.name.split('.').pop()?.toLowerCase());
const fileIcon = computed(() => {
  if (['png', 'jpg', 'jpeg', 'svg', 'gif'].includes(extension.value ?? '')) return 'file-image';
  if (['csv', 'xls', 'xlsx'].includes(extension.value ?? '')) return 'file-spreadsheet';
  if (['pdf', 'txt', 'doc', 'docx'].includes(extension.value ?? '')) return 'file-lines';
  return 'file';
});
const fileSize = computed(() => {
  if (props.size === undefined) return;
  if (!props.size) return '0 Bytes';
  const unit = Math.floor(Math.log(props.size) / Math.log(1024));
  return `${parseFloat((props.size / Math.pow(1024, unit)).toFixed(1))} ${['Bytes', 'KiB', 'MiB', 'GiB'][unit]}`;
});
</script>

<template>
  <div class="ps-file ps-ui-element">
    <div class="file-type">
      <FontAwesomeIcon class="file-icon" :icon="['fal', fileIcon]" fixed-width size="lg" />
    </div>
    <div class="file-title-tools-meta">
      <a v-if="link" class="file-name file-link" :href="link" target="_blank">{{ name }}</a>
      <button v-else-if="clickable" type="button" class="file-name file-link file-button" @click="emit('click')">{{ name }}</button>
      <span v-else class="file-name">{{ name }}</span>
      <span v-if="uploadedAt || size" class="file-meta">
        <span v-if="uploadedAt">Uploaded At: {{ formatCultureDate(uploadedAt) }}</span>
        <span v-if="size">Size: {{ fileSize }}</span>
      </span>
    </div>
  </div>
</template>

<style scoped>
.ps-file,
.file-title-tools-meta,
.file-meta {
  align-items: center;
  display: inline-flex;
}

.ps-file {
  gap: 0.35rem;
}

.file-type,
.file-icon {
  flex: 0 0 1.25rem;
  height: 1.25rem !important;
  width: 1.25rem !important;
}

.file-icon {
  display: block;
}

.file-title-tools-meta {
  gap: 0.5rem;
  min-width: 0;
}

.file-link {
  color: var(--ps-ui-text-link);
}

.file-button {
  background: none;
  border: 0;
  cursor: pointer;
  font: inherit;
  padding: 0;
  text-align: left;
}

.file-meta {
  font-size: 0.75rem;
  gap: 0.25rem;
  opacity: 60%;
  white-space: nowrap;
}
</style>
